/**
 * Every field kind shares the same outer shell: `mdy-renderer`, `mdy-label`, `mdy-input-wrapper`,
 * `mdy-control__errors`. The structure is the contract's, and the shipped `@modyra/styles` themes
 * target exactly these classes — a renderer that spells them differently produces a form the themes
 * cannot style.
 *
 * Renderers build the control themselves and insert it into the wrapper.
 */
import { MDY_FIELD_SHELL_CLASSES, MDY_WIDGET_CONTRACTS, fieldAccessibleName, fieldAriaLabel, shellStateClasses } from "@modyra/widgets";
import { fieldCanBeInvalid } from "@modyra/widgets";
import { el, setText } from "./dom.js";
export function buildFieldShell(labelText, kind, affixes = {}, ariaLabel, 
/** The field's own name, which names the control when nothing written for a person does. */
fieldName, 
/** The line under the control, when the field declares one. */
supportingText) {
    const root = el("div");
    root.classList.add(...MDY_WIDGET_CONTRACTS[kind].rootClasses);
    const label = el("label", MDY_FIELD_SHELL_CLASSES.label);
    // A label a document did not write still has to say something, because everything inside this
    // shell is named by pointing at it: a `radiogroup`, a `grid`, a `dialog` whose `aria-labelledby`
    // resolves to an empty element is announced as its role and nothing else. The words are the ones
    // `fieldAccessibleName` chooses, and where they are the field's own name rather than a person's,
    // the label is kept out of sight — a name is owed to a screen reader, a heading is not.
    const written = fieldAccessibleName({ ariaLabel, label: labelText, name: fieldName });
    if (labelText)
        setText(label, labelText);
    else if (written) {
        setText(label, written);
        label.classList.add("mdy-label--unwritten");
    }
    // Built once and attached only while the field is required: `syncState` is what puts it in the
    // label and what takes it out.
    const requiredMark = el("span", MDY_FIELD_SHELL_CLASSES.requiredMarker);
    setText(requiredMark, "*");
    // Decoration, not words: the caption names the control, and `aria-required` already says the field
    // must be filled. Left readable, the name becomes "Email address *". ADR 0214.
    requiredMark.setAttribute("aria-hidden", "true");
    /**
     * The box, on the kinds whose value is read inside one.
     *
     * Given to every field, it framed a slider's track and a radio group's dots — controls whose slot
     * *is* the value, with nothing to look into. The three renderers then disagreed about which of them
     * wore it, because each decided separately what the shell handed out unconditionally.
     *
     * Asked of the contract, so no renderer decides: `valueSlot` says whether this kind is read by
     * looking inside a surface. The wrapper element stays either way — it is the row the shell lays
     * out — and what it stops carrying is the treatment.
     */
    const readsInsideASurface = MDY_WIDGET_CONTRACTS[kind].valueSlot === "container";
    const wrapper = el("div", readsInsideASurface ? MDY_FIELD_SHELL_CLASSES.inputWrapper : "");
    // The themes lay the wrapper out as a flex row and expect the control inside an inliner —
    // without it the control is a flex item with no basis and collapses to nothing.
    const inliner = el("div", MDY_FIELD_SHELL_CLASSES.control);
    /**
     * A press that lands on the box rather than on the control still reaches the control.
     *
     * The inliner is inset from the field it sits in, so there is a strip along each edge that looks
     * like the field and is not the control: a press there put focus nowhere at all — measured, the
     * document's body kept it — while the same press in the other two renderers lands on the control,
     * because neither of them draws this element.
     *
     * Forwarded rather than removed: the themes lay the wrapper out as a flex row and expect this box,
     * and a control that is a flex item with no basis collapses to nothing. Only a press on the box
     * *itself* is forwarded — one on a prefix, a suffix or a button inside it is that element's.
     */
    inliner.addEventListener("mousedown", (event) => {
        if (event.target !== inliner)
            return;
        const control = inliner.querySelector("input, textarea, select, button");
        if (control === null || control.hasAttribute("disabled"))
            return;
        event.preventDefault();
        control.focus();
    });
    // Only when there is something to put in them: an empty affix is a gap the theme still spaces.
    if (affixes.prefix) {
        const prefix = el("div", MDY_FIELD_SHELL_CLASSES.prefix);
        setText(prefix, affixes.prefix);
        wrapper.appendChild(prefix);
    }
    wrapper.appendChild(inliner);
    if (affixes.suffix) {
        const suffix = el("div", MDY_FIELD_SHELL_CLASSES.suffix);
        setText(suffix, affixes.suffix);
        wrapper.appendChild(suffix);
    }
    const description = el("p", MDY_FIELD_SHELL_CLASSES.supportingText);
    // Empty until a field declares words for it. Hidden while it is, because a slot with nothing in it
    // is height a person cannot read, and `aria-describedby` naming an empty element sends a screen
    // reader somewhere and gives it nothing to say.
    // The words are the projection's answer and are written by `applyPart` when the part is applied,
    // so they are not written here as well: two writers put the sentence in twice. What the shell
    // still decides is whether the slot takes up room, because a paragraph with nothing in it is
    // height a person cannot read.
    description.hidden = !supportingText;
    const errorList = el("ul", MDY_FIELD_SHELL_CLASSES.errors);
    root.append(label, wrapper, description, errorList);
    return {
        ariaLabel,
        labelText,
        fieldName,
        kind,
        root,
        label,
        wrapper,
        description,
        errorList,
        syncState({ touched, disabled, hasError, filled, required, constraints, open, readonly }) {
            // Two questions, and only one of them is about the field's rules.
            //
            // The container **holds its place** under any field that can fail a rule of its own, so a
            // message arriving does not push down the field somebody is already reaching for. Under a field
            // with no rule that is a line of scrolling bought for nothing.
            //
            // But a field with no local rule can still be told it is wrong: a server answers about a value
            // it alone can judge, and that refusal has to land somewhere. Reserved-or-not decides whether
            // the box waits empty; **having something to say decides whether the box exists at all**, and
            // reading only the first hid a message the form was holding.
            errorList.hidden = !hasError && !fieldCanBeInvalid({ required, constraints, disabled });
            // Which class a state puts on is the contract's answer, not this file's — including that a
            // failing field is `--error` on its wrapper and `--has-error` on its label, two spellings a
            // reader had to know were one state. `readonly` is a different refusal from `disabled` and has
            // to look like one, or a form held for review reads as one waiting to be filled in.
            // `unwritten` among them, and it is not cosmetic: the caption a document did not write carries
            // the field's own key, and the class is what keeps that key out of sight while leaving it where
            // a reader can follow a reference to it. Left out of this call, the toggle below turned off the
            // class the shell had just switched on — so a page showed `rows.0.code` in the position and
            // styling of a caption somebody meant, which is worse than showing nothing at all.
            const shell = shellStateClasses({
                touched, open, disabled, readonly, error: hasError, filled, unwritten: !labelText,
            });
            for (const [element, wanted] of [[root, shell.field], [wrapper, shell.control], [label, shell.label]]) {
                for (const [className, isOn] of Object.entries(wanted))
                    element.classList.toggle(className, isOn);
            }
            // Present only where it applies. Hidden was not enough: the element was still in the label
            // for anything asking whether this field is marked — a test, a tool, a stylesheet — so an
            // optional field carried the marker of a required one and only `display: none` said otherwise.
            if (required) {
                if (requiredMark.parentElement === null)
                    label.appendChild(requiredMark);
            }
            else {
                requiredMark.remove();
            }
        },
    };
}
/**
 * Puts the control inside the input wrapper, where every renderer and every theme expects it.
 *
 * This is also where the control gets an `aria-label`, and only where one is owed: the name a
 * document declared, or the field's own name when there is no caption to name it. A caption with
 * words names the control through `for`, and a copy written here would replace that association
 * rather than add to it. ADR 0214.
 */
export function insertControl(shell, control) {
    // A control with no accessible name is announced as its role and nothing else. A label is optional
    // in a document by design, so the field's own name is what is left to say — see
    // `fieldAriaLabel`, which is where the order lives so every renderer answers the same.
    const name = fieldAriaLabel({
        ariaLabel: shell.ariaLabel,
        label: shell.labelText,
        name: shell.fieldName,
    });
    // On the element a person operates, which is not always the one handed over: a slider arrives
    // wrapped in its track, and a name on the wrapper is a name the control does not carry.
    //
    // A real control before a bare `[role]`, and that order is the whole of it. Asked for either at
    // once, a multiselect handed over its box and the first match was the **chip strip** — a
    // `role="list"`, structure rather than control — so the field's name was announced on the list of
    // chosen values while the combobox beside it carried the same word. One name, two things, and the
    // renderer that did it was the only one of three that did.
    const operable = "input, select, textarea, button";
    // **The element the contract calls this kind's control**, not the first one that happens to match.
    // A file field hands over a container whose first control is the hidden picker — which is the
    // control — and a radio group hands over a container whose first control is an arbitrary option,
    // where the caption was announced as the name of "Small" and every other option had none. Counting
    // them does not tell the two apart; the catalogue does, because a kind that has a `control` part
    // says which element it is and a kind that has none is named as a whole.
    const declared = MDY_WIDGET_CONTRACTS[shell.kind].parts;
    const controlClass = declared.control?.classes[0];
    // No `CSS.escape`: it is absent in some of the environments this runs in, and these class names
    // are the catalogue's own — a fixed vocabulary with nothing in it a selector has to be protected
    // from.
    const named = controlClass ? control.querySelector(`.${controlClass}`) : null;
    // **The one control that holds the value**, before the fallbacks below. A stepper is a button and
    // an action, with a name of its own saying what pressing it does; the box beside it is the field.
    // Where a container offers exactly one operable that is not a button, that is the field's control,
    // whatever else sits around it.
    //
    // Without this a number — an input with a stepper either side — matched no earlier branch and
    // reached the last one, which names the **container**: a `<span>` with no role, not focusable,
    // never announced. The document declared a name, a `<span>` nothing could reach carried the words,
    // and the box a person lands on had none. It survived because no check in any tier asked a number
    // for its declared name; the one that asks mounts a text field.
    const valueBearing = control.querySelectorAll("input:not([type=button]), select, textarea");
    const holdsTheValue = valueBearing.length === 1 ? valueBearing[0] : null;
    const operated = control.matches(`${operable}, [role]`)
        ? control
        : named
            ?? holdsTheValue
            ?? control.querySelector("[role]")
            ?? (control.querySelectorAll(operable).length === 1
                ? control.querySelector(operable)
                : control);
    // The field's name goes on the element that stands for the field — unless that element has
    // already said what it is. A door into a panel is named for the act it performs, from the
    // dictionary, and writing the field's label over it produces "T, button": the caption repeated,
    // saying nothing about what pressing it does, and in English on a translated page.
    if (name && !operated.hasAttribute("aria-label"))
        operated.setAttribute("aria-label", name);
    (shell.wrapper.querySelector(`.${MDY_FIELD_SHELL_CLASSES.control}`) ?? shell.wrapper).appendChild(control);
}
